import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { InfiniteScroll, NoResults } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import { Fetching, globalActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import StatusRowMobile from './StatusRowMobile';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Else, If, Then, When } from 'react-if';

import './MainViewBlockMobile.scss';

const REQUEST_ID = "Main_Mobile_Statuses"
const REFRESH_TIME_STATUS_MAIN = 60_000 // miliseconds
const useSearchStatuses = ({ query, isJobId, category, selector, getQueryBuilder, shouldBeLimited }) =>
  useInfiniteQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: isJobId ? "*" : (query ? `*${query}*` : "*"), 
        size: 50, 
        aggregate: true,
        type: statusesActions.CATEGORIES[category.toUpperCase()],
        jobId: isJobId ? query : undefined,
        [selector]: !!selector,
        REQUEST_ID
      }
    ),
    initialPageParam: 0,
    refetchInterval: () => shouldBeLimited() ? undefined : REFRESH_TIME_STATUS_MAIN,
    getNextPageParam: CommonService.getNextPageParam,
    enabled: !!category
  })
const MainViewBlockMobile = ({ category = "", query = "", selector = "" }) => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        scrollContRef = useRef(),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        isJobId = useMemo(() => query.match("([a-f0-9]{8}(-[a-f0-9]{4}){4}[a-f0-9]{8})"), [query]),
        { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchStatuses({ query, isJobId, category, selector, getQueryBuilder, shouldBeLimited }),
        onScroll = useCallback(() => fetchNextPage(), [fetchNextPage])

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <div className="statuses-main-view-block-mobile">
      <When condition={isFetchingNextPage || isPending}>
        <Fetching/>
      </When>
      <If condition={data?.pages?.length === 1 && !isPending && data?.pages?.[0]?.items.length === 0}>
        <Then>
          {() => (
            <div className="statuses-main-view-block-mobile__no-results">
              <NoResults/>
            </div>
          )}  
        </Then>
        <Else>
          {() => (
            <div 
                className="statuses-main-view-block-mobile__container" 
                ref={scrollContRef}
            >
              <InfiniteScroll
                  className="statuses-main-view-block-mobile__infinite-scroll"
                  hasMore={hasNextPage}
                  initialLoad={false}
                  loadMore={onScroll}
                  threshold={800}
                  useWindow={false}
              >
                {(data?.pages || []).map((page, pageIndex) => (
                  <Fragment key={pageIndex}>
                    {page.items.map(({ jobId, name, payload, status, type, types }, index) => (
                      <StatusRowMobile
                          category={category}
                          groupName={payload?.groupName}
                          jobId={jobId}
                          key={"Status " + jobId + index}
                          name={category === StatusesEnums.STATUS_TYPE.ETL ? (payload?.groupName || name) : name}
                          scrollContRef={scrollContRef}
                          status={status}
                          type={type}
                          types={types}
                          version={payload?.version}
                      />
                    ))}
                  </Fragment>
                ))}
              </InfiniteScroll>
            </div>
          )}  
        </Else>
      </If>
    </div>
  )
}

MainViewBlockMobile.propTypes = {
  category: PropTypes.string,
  query: PropTypes.string,
  selector: PropTypes.string
}

export default MainViewBlockMobile;